import * as React from 'react';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import { confirmDialog } from 'primereact/confirmdialog';

import { alertSuccess } from '../../../../store/reducers/Alert';
import { services } from '../../../../services';
import {
    useBackgroundPromiseAllWrapper,
    useBackgroundPromiseWrapper
} from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../../helpers/hooks/useDownload';
import { useBackgroundPromiseEffectByCondition } from '../../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { handleError } from '../../../../helpers/hooks/handleError';
import { ProjectDocSignerRoleCode } from '../../models/enums/ProjectDocSignerRoleCode';
import { ProjectDocSigner } from '../../models/ProjectDocSigner';
import { ActTemplate } from '../../models/ActTemplate';
import { ProjectDocFlowType } from '../../models/enums/ProjectDocFlowType';

const isNrssIdRequired = (signer: Partial<ProjectDocSigner>): boolean =>
    signer.RoleId !== ProjectDocSignerRoleCode.Contractor && signer.RoleId !== ProjectDocSignerRoleCode.SkContractor;

const getRepFields = (signer: Partial<ProjectDocSigner>, electronic: boolean): (keyof ProjectDocSigner)[] => {
    const fields: (keyof ProjectDocSigner)[] = [
        'RepJobTitle',
        'RepLastName',
        'RepFirstName',
        'RepMiddleName',
        'AuthorityDocType',
        'AuthorityDocDetails'
    ];
    if (electronic) fields.push('RepInn');
    if (isNrssIdRequired(signer)) fields.push('NrssId');
    return fields;
};

export const isActPartyEmpty = (signer: Partial<ProjectDocSigner>): boolean => {
    if (signer.RoleId !== ProjectDocSignerRoleCode.ActParty) return false;
    if ((signer.ActTypeBindings ?? []).length > 0) return false;
    const fields: (keyof ProjectDocSigner)[] = [
        'OrgName',
        'Ogrn',
        'InnOrg',
        'Address',
        'PhoneFax',
        'EdiProviderId',
        'RepJobTitle',
        'RepLastName',
        'RepFirstName',
        'RepMiddleName',
        'AuthorityDocType',
        'AuthorityDocDetails',
        'NrssId',
        'RepInn'
    ];
    return !fields.some((f) => !!(signer as any)[f]) && !signer.BasisDocumentId && !signer.BasisDocumentUpload?.Content;
};

export const isActiveSigner = (signer: Partial<ProjectDocSigner>): boolean =>
    signer.RoleId !== ProjectDocSignerRoleCode.ActParty || !isActPartyEmpty(signer);

function computeFieldErrors(signersList: Partial<ProjectDocSigner>[], electronic: boolean): Set<string> {
    const errors = new Set<string>();
    const orgFields: (keyof ProjectDocSigner)[] = ['OrgName', 'Ogrn', 'InnOrg', 'Address', 'PhoneFax'];
    if (electronic) orgFields.push('EdiProviderId');
    signersList.forEach((signer, index) => {
        if (!isActiveSigner(signer)) return;
        const isDesigner = signer.RoleId === ProjectDocSignerRoleCode.Designer;
        const needsRep = !isDesigner || !!signer.HasAuthorSupervision;
        orgFields.forEach((f) => {
            if (!(signer as any)[f]) errors.add(`${index}_${f}`);
        });
        if (needsRep) {
            getRepFields(signer, electronic).forEach((f) => {
                if (!(signer as any)[f]) errors.add(`${index}_${f}`);
            });
            if (!signer.BasisDocumentId && !signer.BasisDocumentUpload?.Content) errors.add(`${index}_BasisDocument`);
        }
        if (signer.RoleId === ProjectDocSignerRoleCode.ActParty && (signer.ActTypeBindings ?? []).length === 0) {
            errors.add(`${index}_ActTypeBindings`);
        }
    });
    return errors;
}

export interface UseSignerFormOptions {
    projectDocId: number;
    contractorId?: number;
    readonly: boolean;
    projectDocFlowType: ProjectDocFlowType;
    onSaved?: () => void;
    onDirtyChange?: (isDirty: boolean) => void;
}

export interface SignerCompletion {
    filled: number;
    total: number;
}

export const useSignerForm = ({
    projectDocId,
    contractorId,
    readonly,
    projectDocFlowType,
    onSaved,
    onDirtyChange
}: UseSignerFormOptions) => {
    const dispatch = useDispatch();
    const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();
    const { downloadFile, isDownloading: isDownloadingDocument } = useDownload();
    const [promiseAll] = useBackgroundPromiseAllWrapper();
    const [rawSigners, setRawSigners] = React.useState<ProjectDocSigner[]>([]);
    const rawSignersProjectDocId = React.useRef<number | null>(null);
    const [readyProjectDocId, setReadyProjectDocId] = React.useState<number | null>(null);
    const [contractor, setContractor] = React.useState<any>();
    const [validationErrors, setValidationErrors] = React.useState<Set<string>>(new Set());
    const [actTypes, setActTypes] = React.useState<ActTemplate[]>([]);
    const [signers, setSigners] = React.useState<Partial<ProjectDocSigner>[]>([]);
    const contractorFilledFieldsRef = React.useRef<Set<string>>(new Set());
    const userSetElectronic = React.useRef(false);
    const [isElectronic, setIsElectronic] = React.useState(projectDocFlowType === ProjectDocFlowType.Electronic);
    const [dirtyIndices, setDirtyIndices] = React.useState<Set<number>>(new Set());
    const [reloadKey, setReloadKey] = React.useState(0);
    const dirtyRef = React.useRef(false);
    const revisionRef = React.useRef(0);
    const currentProjectDocId = React.useRef(projectDocId);
    currentProjectDocId.current = projectDocId;

    const markDirty = React.useCallback(() => {
        dirtyRef.current = true;
        revisionRef.current++;
        onDirtyChange?.(true);
    }, [onDirtyChange]);

    React.useEffect(() => {
        if (!userSetElectronic.current) {
            setIsElectronic(projectDocFlowType === ProjectDocFlowType.Electronic);
        }
    }, [projectDocFlowType]);

    React.useEffect(() => {
        userSetElectronic.current = false;
        setIsElectronic(projectDocFlowType === ProjectDocFlowType.Electronic);
        dirtyRef.current = false;
        revisionRef.current++;
        onDirtyChange?.(false);
        rawSignersProjectDocId.current = null;
        setReadyProjectDocId(null);
        setRawSigners([]);
        setDirtyIndices(new Set());
        setValidationErrors(new Set());
    }, [projectDocId, onDirtyChange]);

    React.useEffect(() => {
        let cancelled = false;
        setContractor(undefined);
        setActTypes([]);
        if (!contractorId) return;
        promiseAll(
            [
                () => services.actTemplateService.fetch({ Take: 9999, IsDeleted: false }),
                () => services.contractorsService.GetBriefById(contractorId)
            ],
            ([actTypesData, contractorData]) => {
                if (cancelled) return;
                setActTypes(actTypesData ?? []);
                setContractor(contractorData);
            }
        );
        return () => { cancelled = true; };
    }, [projectDocId, contractorId, promiseAll]);

    React.useEffect(() => {
        return () => {
            onDirtyChange?.(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useBackgroundPromiseEffectByCondition(
        () => projectDocId > 0,
        () => services.projectDocSignerService.fetch({ ProjectDocId: projectDocId, IsDeleted: false }),
        (data: ProjectDocSigner[]) => {
            rawSignersProjectDocId.current = projectDocId;
            setRawSigners(data);
        },
        [projectDocId, reloadKey]
    );

    React.useEffect(() => {
        if (dirtyRef.current) return;
        contractorFilledFieldsRef.current = new Set();
        const result: Partial<ProjectDocSigner>[] = [];
        const defaultRoleCodes = [
            ProjectDocSignerRoleCode.Contractor,
            ProjectDocSignerRoleCode.Designer,
            ProjectDocSignerRoleCode.SkContractor
        ];
        for (let i = 0; i < defaultRoleCodes.length; i++) {
            const roleCode = defaultRoleCodes[i];
            const existing = rawSigners.find((s) => s.RoleId === roleCode);
            if (existing) {
                result.push(existing);
                if (
                    roleCode === ProjectDocSignerRoleCode.Contractor ||
                    roleCode === ProjectDocSignerRoleCode.SkContractor
                ) {
                    if (existing.OrgName) contractorFilledFieldsRef.current.add(`${i}_OrgName`);
                    if (existing.InnOrg) contractorFilledFieldsRef.current.add(`${i}_InnOrg`);
                    if (existing.PhoneFax) contractorFilledFieldsRef.current.add(`${i}_PhoneFax`);
                    if (existing.Ogrn) contractorFilledFieldsRef.current.add(`${i}_Ogrn`);
                    if (existing.Address) contractorFilledFieldsRef.current.add(`${i}_Address`);
                }
            } else {
                const signer: Partial<ProjectDocSigner> = {
                    ProjectDocId: projectDocId,
                    RoleId: roleCode,
                    SortOrder: roleCode
                };
                if (
                    contractor &&
                    (roleCode === ProjectDocSignerRoleCode.Contractor ||
                        roleCode === ProjectDocSignerRoleCode.SkContractor)
                ) {
                    signer.OrgName = contractor.Title ?? '';
                    signer.InnOrg = contractor.INN ?? '';
                    signer.PhoneFax = contractor.Phone ?? '';
                    signer.Ogrn = contractor.OGRN ?? '';
                    signer.Address = contractor.FactAddress || contractor.LegalAddress || '';

                    if (signer.OrgName) contractorFilledFieldsRef.current.add(`${i}_OrgName`);
                    if (signer.InnOrg) contractorFilledFieldsRef.current.add(`${i}_InnOrg`);
                    if (signer.PhoneFax) contractorFilledFieldsRef.current.add(`${i}_PhoneFax`);
                    if (signer.Ogrn) contractorFilledFieldsRef.current.add(`${i}_Ogrn`);
                    if (signer.Address) contractorFilledFieldsRef.current.add(`${i}_Address`);
                }
                result.push(signer);
            }
        }

        for (let i = 0; i < rawSigners.length; i++) {
            const s = rawSigners[i];
            if (s.RoleId === ProjectDocSignerRoleCode.ActParty || s.RoleId === ProjectDocSignerRoleCode.Other) {
                result.push({
                    ...s,
                    ActTypeBindings: ((s.ActTypeBindings ?? []) as any[]).map((b: any) =>
                        typeof b === 'number' ? b : b.ActTypeId
                    )
                });
            }
        }
        setSigners(result);
        setDirtyIndices(new Set());
        if (rawSignersProjectDocId.current === projectDocId) setReadyProjectDocId(projectDocId);
    }, [rawSigners, contractor, projectDocId]);

    const updateSigner = React.useCallback(
        (index: number, field: keyof ProjectDocSigner, value: any) => {
            setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
            setValidationErrors((prev) => {
                const key = `${index}_${field as string}`;
                if (!prev.has(key)) return prev;
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            markDirty();
            setDirtyIndices((prev) => {
                const next = new Set(prev);
                next.add(index);
                return next;
            });
        },
        [markDirty]
    );

    const setSignerFields = React.useCallback(
        (index: number, patch: Partial<ProjectDocSigner>) => {
            setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
            setValidationErrors((prev) => {
                const keys = Object.keys(patch).map((k) => `${index}_${k}`);
                if (!keys.some((k) => prev.has(k))) return prev;
                const next = new Set(prev);
                keys.forEach((k) => next.delete(k));
                return next;
            });
            markDirty();
            setDirtyIndices((prev) => {
                const next = new Set(prev);
                next.add(index);
                return next;
            });
        },
        [markDirty]
    );

    const setElectronic = React.useCallback(
        (value: boolean) => {
            userSetElectronic.current = true;
            setIsElectronic(value);
            markDirty();
        },
        [markDirty]
    );

    const reload = React.useCallback(() => {
        dirtyRef.current = false;
        setValidationErrors(new Set());
        userSetElectronic.current = false;
        setIsElectronic(projectDocFlowType === ProjectDocFlowType.Electronic);
        onDirtyChange?.(false);
        setReloadKey((k) => k + 1);
    }, [onDirtyChange, projectDocFlowType]);

    const getLabel = React.useCallback((signer: Partial<ProjectDocSigner>): string => {
        if (signer.RoleId === ProjectDocSignerRoleCode.Contractor)
            return 'Лицо, осуществляющее строительство (генподрядчик)';
        if (signer.RoleId === ProjectDocSignerRoleCode.Designer)
            return 'Лицо, осуществляющее подготовку проектной документации';
        if (signer.RoleId === ProjectDocSignerRoleCode.SkContractor)
            return 'Лицо, осуществляющее строительство по вопросам СК (СК генподрядчика)';
        if (signer.RoleId === ProjectDocSignerRoleCode.ActParty) return 'Лицо, выполнившее работы';
        if (signer.RoleId === ProjectDocSignerRoleCode.Other)
            return 'Иное участвующее лицо' + (signer.CustomRoleName ? ' — ' + signer.CustomRoleName : '');
        return '';
    }, []);

    const getSubLabel = React.useCallback((signer: Partial<ProjectDocSigner>): string => {
        if (signer.RoleId === ProjectDocSignerRoleCode.Contractor) return 'Генподрядчик';
        if (signer.RoleId === ProjectDocSignerRoleCode.Designer) return 'Проектировщик';
        if (signer.RoleId === ProjectDocSignerRoleCode.SkContractor) return 'СК генподрядчика';
        if (signer.RoleId === ProjectDocSignerRoleCode.ActParty) return 'Исполнитель работ';
        if (signer.RoleId === ProjectDocSignerRoleCode.Other) return 'Иное лицо';
        return 'Лицо';
    }, []);

    const addActParty = React.useCallback(() => {
        setSigners((prev) => {
            const maxSort = prev.reduce((max, s) => Math.max(max, s.SortOrder ?? 0), 0);
            return [
                ...prev,
                {
                    ProjectDocId: projectDocId,
                    RoleId: ProjectDocSignerRoleCode.ActParty,
                    SortOrder: maxSort + 1,
                    ActTypeBindings: []
                }
            ];
        });
        markDirty();
    }, [projectDocId, markDirty]);

    const addOther = React.useCallback(() => {
        setSigners((prev) => {
            const maxSort = prev.reduce((max, s) => Math.max(max, s.SortOrder ?? 0), 0);
            return [
                ...prev,
                {
                    ProjectDocId: projectDocId,
                    RoleId: ProjectDocSignerRoleCode.Other,
                    SortOrder: maxSort + 1
                }
            ];
        });
        markDirty();
    }, [projectDocId, markDirty]);

    const removeSigner = React.useCallback(
        (index: number) => {
            const signer = signers[index];
            if (signer?.Id) {
                promiseWrapper(
                    () => services.projectDocSignerService.delete([signer.Id!]),
                    () => setSigners((prev) => prev.filter((_, i) => i !== index))
                );
            } else {
                setSigners((prev) => prev.filter((_, i) => i !== index));
            }
        },
        [signers, promiseWrapper]
    );

    const save = React.useCallback(() => {
        const savedRevision = revisionRef.current;
        const toSave = signers
            .filter((s) => s.RoleId != null && isActiveSigner(s))
            .map((s) => ({
                ...s,
                BasisDocument: undefined,
                ActTypeBindings: [],
                ActTypeIds: s.ActTypeBindings ?? [],
                DocFlowType: isElectronic ? ProjectDocFlowType.Electronic : ProjectDocFlowType.Paper
            })) as unknown as ProjectDocSigner[];

        const doSave = () => {
            if (currentProjectDocId.current !== projectDocId) return;
            const emptyActPartyIds = signers
                .filter((s) => s.RoleId === ProjectDocSignerRoleCode.ActParty && isActPartyEmpty(s) && s.Id)
                .map((s) => s.Id!);

            const savePromise =
                emptyActPartyIds.length > 0
                    ? services.projectDocSignerService
                          .delete(emptyActPartyIds)
                          .then(() => services.projectDocSignerService.save(toSave))
                    : services.projectDocSignerService.save(toSave);

            promiseWrapper(
                () => savePromise,
                () => {
                    if (currentProjectDocId.current !== projectDocId) return;
                    setValidationErrors(new Set());
                    dispatch(alertSuccess({ detail: 'Сохранено' }));
                    if (revisionRef.current === savedRevision) {
                        dirtyRef.current = false;
                        onDirtyChange?.(false);
                        userSetElectronic.current = false;
                        if (!isElectronic) {
                            setSigners((prev) => prev.map((s) => ({ ...s, EdiProviderId: undefined, RepInn: undefined })));
                        }
                    }

                    services.projectDocSignerService
                        .fetch({ ProjectDocId: projectDocId, IsDeleted: false })
                        .then((data: AxiosResponse<ProjectDocSigner[]>) => {
                            if (currentProjectDocId.current === projectDocId) setRawSigners(data.data);
                        })
                        .catch((error: unknown) => {
                            if (currentProjectDocId.current === projectDocId) handleError(error, dispatch);
                        });
                    onSaved?.();
                }
            );
        };

        promiseWrapper(
            () => services.projectDocSignerService.validate(toSave),
            (errors: string[]) => {
                if (currentProjectDocId.current !== projectDocId) return;
                if (errors.length === 0) {
                    setValidationErrors(new Set());
                    doSave();
                } else {
                    setValidationErrors(computeFieldErrors(signers, isElectronic));
                    confirmDialog({
                        header: 'Заполните форму',
                        message:
                            'Форма заполнена не полностью. Акты, в которых используются эти данные, не будут формироваться.',
                        icon: 'fa-solid fa-triangle-exclamation',
                        className: 'pd-dialog',
                        acceptLabel: 'Сохранить',
                        acceptIcon: 'fa-solid fa-check',
                        rejectLabel: 'Отмена',
                        rejectIcon: 'fa-solid fa-arrow-left',
                        acceptClassName: 'pd-confirm-btn-accept',
                        rejectClassName: 'pd-confirm-btn-reject',
                        accept: doSave
                    });
                }
            }
        );
    }, [signers, promiseWrapper, dispatch, isElectronic, projectDocId, onDirtyChange, onSaved]);

    const handleFileUpload = React.useCallback(
        async (index: number, file: File) => {
            if (!file) {
                updateSigner(index, 'BasisDocumentUpload', undefined);
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = function () {
                const data = reader.result as string;
                const m = ';base64,';
                const i = data.indexOf(m);
                const content = data.substring(i + m.length);
                setSigners((prev) =>
                    prev.map((s, si) =>
                        si === index
                            ? {
                                  ...s,
                                  BasisDocumentId: undefined,
                                  BasisDocumentUpload: {
                                      FileName: file.name,
                                      Content: content,
                                      Size: file.size,
                                      ContentType: file.type
                                  }
                              }
                            : s
                    )
                );
                setValidationErrors((prev) => {
                    const key = `${index}_BasisDocument`;
                    if (!prev.has(key)) return prev;
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
                markDirty();
                setDirtyIndices((prev) => {
                    const next = new Set(prev);
                    next.add(index);
                    return next;
                });
            };
        },
        [updateSigner, markDirty]
    );

    const removeBasisDocument = React.useCallback(
        (index: number) => {
            setSignerFields(index, { BasisDocumentId: undefined, BasisDocumentUpload: undefined });
            setDirtyIndices(new Set());
        },
        [setSignerFields]
    );

    const downloadBasisDocument = React.useCallback(
        (signerId: number) => {
            void downloadFile(`ProjectDocSigners/DownloadBasisDocument?signerId=${signerId}`);
        },
        [downloadFile]
    );

    const getAvailableActTypes = React.useCallback(
        (currentIndex: number): ActTemplate[] => {
            const usedInOthers = new Set<number>();
            for (let i = 0; i < signers.length; i++) {
                if (i === currentIndex) continue;
                if (signers[i].RoleId !== ProjectDocSignerRoleCode.ActParty) continue;
                const bindings = signers[i].ActTypeBindings ?? [];
                for (let j = 0; j < bindings.length; j++) usedInOthers.add(bindings[j] as number);
            }
            return actTypes.filter((at) => at.Id !== undefined && !usedInOthers.has(at.Id));
        },
        [signers, actTypes]
    );

    const contractorLocked = React.useCallback(
        (index: number, field: string) => {
            const s = signers[index];
            const isContractorRole =
                s?.RoleId === ProjectDocSignerRoleCode.Contractor ||
                s?.RoleId === ProjectDocSignerRoleCode.SkContractor;
            return isContractorRole && contractorFilledFieldsRef.current.has(`${index}_${field}`);
        },
        [signers]
    );

    const errClass = React.useCallback(
        (index: number, field: string) => validationErrors.has(`${index}_${field}`),
        [validationErrors]
    );

    const completionOf = React.useCallback(
        (signer: Partial<ProjectDocSigner>): SignerCompletion => {
            if (!isActiveSigner(signer)) return { filled: 0, total: 0 };

            const orgFields: (keyof ProjectDocSigner)[] = ['OrgName', 'Ogrn', 'InnOrg', 'Address', 'PhoneFax'];
            if (isElectronic) orgFields.push('EdiProviderId');
            const repFields = getRepFields(signer, isElectronic);

            const isDesigner = signer.RoleId === ProjectDocSignerRoleCode.Designer;
            const needsRep = !isDesigner || !!signer.HasAuthorSupervision;
            const isActParty = signer.RoleId === ProjectDocSignerRoleCode.ActParty;

            const fields: (keyof ProjectDocSigner)[] = [...orgFields, ...(needsRep ? repFields : [])];
            let total = fields.length;
            let filled = fields.reduce((acc, f) => acc + ((signer as any)[f] ? 1 : 0), 0);

            if (needsRep) {
                total += 1; // basis document
                if (signer.BasisDocumentId || signer.BasisDocumentUpload?.Content) filled += 1;
            }
            if (isActParty) {
                total += 1; // at least one act type
                if ((signer.ActTypeBindings ?? []).length > 0) filled += 1;
            }
            return { filled, total };
        },
        [isElectronic]
    );

    const canRemove = React.useCallback(
        (signer: Partial<ProjectDocSigner>) =>
            !readonly &&
            (signer.RoleId === ProjectDocSignerRoleCode.ActParty || signer.RoleId === ProjectDocSignerRoleCode.Other),
        [readonly]
    );

    const isSignerDirty = React.useCallback((index: number) => dirtyIndices.has(index), [dirtyIndices]);

    return {
        signers,
        actTypes,
        isElectronic,
        setElectronic,
        isLoading,
        readonly: readonly || readyProjectDocId !== projectDocId,
        validationErrors,
        updateSigner,
        setSignerFields,
        addActParty,
        addOther,
        removeSigner,
        save,
        reload,
        handleFileUpload,
        removeBasisDocument,
        downloadBasisDocument,
        isDownloadingDocument,
        getAvailableActTypes,
        contractorLocked,
        errClass,
        completionOf,
        canRemove,
        getLabel,
        getSubLabel,
        isSignerDirty
    };
};

export type SignerFormApi = ReturnType<typeof useSignerForm>;
